import { useEffect, useRef } from "react";

/**
 * Keyboard shortcuts for the FloorPlan2D editor.
 *
 * Bindings (all skipped when an INPUT/TEXTAREA/contenteditable has focus):
 *   • Esc                        — commits an in-flight place-by-drag, then clears selection.
 *   • Cmd/Ctrl+Z                 — undo (always, even with nothing selected).
 *   • Arrow Left / Right         — rotate the selected symbol ±5° (Shift = ±15°).
 *                                  Holding the key auto-spins at 60ms intervals.
 *   • 0 or Shift+R               — snap the selected symbol to the nearest 90°.
 *   • Delete / Backspace         — delete the selected symbol.
 *   • [ / ]                      — adjust the selected symbol's 3D height ±0.1m
 *                                  (Shift = ±0.5m).
 *
 * The listener is registered once per `floorIndex` and reads the latest
 * blueprint / selection / placeDragMode via refs so prop changes propagate
 * without re-binding.
 *
 * Extracted from FloorPlan2D.jsx (V2.0.18).
 */
export default function useFloorPlanShortcuts({
  floorIndex,
  blueprint,
  selectedSymbolId,
  placeDragMode,
  setPlaceDragMode,
  setSelectedSymbolId,
  persistSymbols,
  undo,
  onUpdateBlueprint,
}) {
  const blueprintRef = useRef(blueprint);
  blueprintRef.current = blueprint;
  const selectedRef = useRef(selectedSymbolId);
  selectedRef.current = selectedSymbolId;
  const persistRef = useRef(persistSymbols);
  persistRef.current = persistSymbols;
  const undoRef = useRef(undo);
  undoRef.current = undo;
  const placeDragModeRef = useRef(placeDragMode);
  placeDragModeRef.current = placeDragMode;
  const onUpdateRef = useRef(onUpdateBlueprint);
  onUpdateRef.current = onUpdateBlueprint;

  useEffect(() => {
    const isEditable = (el) =>
      el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
    const getList = () => {
      const f = (blueprintRef.current?.floors || [])[floorIndex - 1];
      return Array.isArray(f?.symbols) ? f.symbols : [];
    };
    const rotateBy = (degDelta) => {
      const id = selectedRef.current;
      if (!id) return;
      const stepMagnitude = Math.max(1, Math.abs(degDelta));
      persistRef.current?.(
        getList().map((s) =>
          s.id === id
            ? {
                ...s,
                rotation_deg:
                  (Math.round(
                    ((s.rotation_deg || 0) + degDelta) / stepMagnitude,
                  ) *
                    stepMagnitude +
                    360) %
                  360,
              }
            : s,
        ),
      );
    };

    let holdInterval = null;
    let holdSign = 0;
    const stopHold = () => {
      if (holdInterval) {
        clearInterval(holdInterval);
        holdInterval = null;
        holdSign = 0;
      }
    };

    const onKeyDown = (e) => {
      if (isEditable(document.activeElement)) return;
      if (e.key === "Escape" && placeDragModeRef.current) {
        e.preventDefault();
        if (blueprintRef.current && onUpdateRef.current) {
          onUpdateRef.current(JSON.parse(JSON.stringify(blueprintRef.current)));
        }
        setPlaceDragMode(null);
        return;
      }
      const isUndoKey = (e.key === "z" || e.key === "Z") && (e.metaKey || e.ctrlKey) && !e.shiftKey;
      if (isUndoKey) {
        e.preventDefault();
        undoRef.current?.();
        return;
      }
      if (!selectedRef.current && e.key !== "Escape") return;
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        const step = e.shiftKey ? 15 : 5;
        const sign = e.key === "ArrowLeft" ? -1 : 1;
        if (e.repeat) {
          if (holdSign !== sign) {
            stopHold();
            holdSign = sign;
            holdInterval = setInterval(() => rotateBy(sign * step), 60);
          }
          return;
        }
        rotateBy(sign * step);
      } else if (e.key === "0" || (e.key === "r" && e.shiftKey)) {
        e.preventDefault();
        const id = selectedRef.current;
        if (!id) return;
        persistRef.current?.(
          getList().map((s) =>
            s.id === id
              ? {
                  ...s,
                  rotation_deg:
                    (Math.round((s.rotation_deg || 0) / 90) * 90 + 360) % 360,
                }
              : s,
          ),
        );
      } else if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        const id = selectedRef.current;
        if (!id) return;
        persistRef.current?.(getList().filter((s) => s.id !== id));
        setSelectedSymbolId(null);
      } else if (e.key === "[" || e.key === "]") {
        e.preventDefault();
        const id = selectedRef.current;
        if (!id) return;
        const step = (e.shiftKey ? 0.5 : 0.1) * (e.key === "[" ? -1 : 1);
        persistRef.current?.(
          getList().map((s) => {
            if (s.id !== id) return s;
            const cur = Number.isFinite(s.override_h_m) ? s.override_h_m : 1.0;
            const next = Math.min(6, Math.max(0.05, cur + step));
            return { ...s, override_h_m: Math.round(next * 100) / 100 };
          }),
        );
      } else if (e.key === "Escape") {
        setSelectedSymbolId(null);
      }
    };

    const onKeyUp = (e) => {
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") stopHold();
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      stopHold();
    };
  }, [floorIndex, setPlaceDragMode, setSelectedSymbolId]);
}
